// guard-patrol.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as fromGuardsActions from '../actions/guard-patrol.actions';
import { Store } from '@ngrx/store';
import { PatrollingService } from 'src/app/services/patrolling/patrolling.service';

@Injectable()
export class GuardPatrolEffects {
  loadUserPatrolData$ = createEffect(() =>
    this.actions$.pipe(
      ofType('[Guard Patrol] Load User Patrol Data'),
      switchMap(() =>
        this.patrollingService.getUserPatrolData().pipe(
          map((data) => new fromGuardsActions.LoadUserPatrolDataSuccess(data)),
          catchError((error) =>
            of(
              new fromGuardsActions.LoadUserPatrolDataFailure(
                error?.error?.error || 'Failed to load patrol data'
              )
            )
          )
        )
      )
    )
  );

  startPatrolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType('[Guard Patrol] Start Patrolling'),
      switchMap((action: fromGuardsActions.StartPatrolling) =>
        this.patrollingService
          .startPatrolling(
            action.payload.geofenceId,
            action.payload.startLatitude,
            action.payload.startLongitude
          )
          .pipe(
            map(
              (response) =>
                new fromGuardsActions.StartPatrollingSuccess(response.sessionId)
            ),
            tap(() =>
              this.store.dispatch(new fromGuardsActions.LoadUserPatrolData())
            ),
            catchError((error) =>
              of(
                new fromGuardsActions.StartPatrollingFailure(
                  error?.error?.error || 'Failed to start patrolling'
                )
              )
            )
          )
      )
    )
  );

  endPatrolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType('[Guard Patrol] End Patrolling'),
      switchMap((action: fromGuardsActions.EndPatrolling) =>
        this.patrollingService
          .endPatrolling(
            action.payload.sessionId,
            action.payload.endLatitude,
            action.payload.endLongitude,
            action.payload.monitoringData
          )
          .pipe(
            map(() => new fromGuardsActions.EndPatrollingSuccess()),
            tap(() =>
              this.store.dispatch(new fromGuardsActions.LoadUserPatrolData())
            ),
            catchError((error) =>
              of(
                new fromGuardsActions.EndPatrollingFailure(
                  error?.error?.error || 'Failed to end patrolling'
                )
              )
            )
          )
      )
    )
  );

  getPatrolSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType('[Guard Patrol] Get Patrol Sessions'),
      switchMap(() =>
        this.patrollingService.getPatrolSessions().pipe(
          map(
            (sessions) =>
              new fromGuardsActions.GetPatrolSessionsSuccess(sessions)
          ),
          catchError((error) =>
            of(
              new fromGuardsActions.GetPatrolSessionsFailure(
                error?.error?.error || 'Failed to load patrol sessions'
              )
            )
          )
        )
      )
    )
  );

  getUserPatrolSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType('[Guard Patrol] Get User Patrol Sessions'),
      switchMap(() =>
        this.patrollingService.getUserPatrolSessions().pipe(
          map(
            (sessions) =>
              new fromGuardsActions.GetUserPatrolSessionsSuccess(sessions)
          ),
          catchError((error) =>
            of(
              new fromGuardsActions.GetUserPatrolSessionsFailure(
                error?.error?.error || 'Failed to load user patrol sessions'
              )
            )
          )
        )
      )
    )
  );

  getPatrolViolations$ = createEffect(() =>
    this.actions$.pipe(
      ofType('[Guard Patrol] Get Patrol Violations'),
      switchMap(() =>
        this.patrollingService.getPatrolViolations().pipe(
          map(
            (violations) =>
              new fromGuardsActions.GetPatrolViolationsSuccess(violations)
          ),
          catchError((error) =>
            of(
              new fromGuardsActions.GetPatrolViolationsFailure(
                error?.error?.error || 'Failed to load patrol violations'
              )
            )
          )
        )
      )
    )
  );

  recordPatrolViolation$ = createEffect(() =>
    this.actions$.pipe(
      ofType('[Guard Patrol] Record Patrol Violation'),
      switchMap((action: fromGuardsActions.RecordPatrolViolation) =>
        this.patrollingService
          .recordViolation(
            action.payload.sessionId,
            action.payload.geofenceId,
            action.payload.userId,
            action.payload.userName,
            action.payload.violationType,
            action.payload.latitude,
            action.payload.longitude,
            action.payload.distanceOutside,
            action.payload.userRole
          )
          .pipe(
            map(
              (response) =>
                new fromGuardsActions.RecordPatrolViolationSuccess(response)
            ),
            tap(() =>
              this.store.dispatch(new fromGuardsActions.GetPatrolViolations())
            ),
            catchError((error) =>
              of(
                new fromGuardsActions.RecordPatrolViolationFailure(
                  error?.error?.error || 'Failed to record violation'
                )
              )
            )
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private patrollingService: PatrollingService,
    private store: Store
  ) {}
}
